import type { JSX } from '@builder.io/qwik';
import { component$, Slot } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import type { SectionBlockInterface } from '~/types/AppData/PageBuilder/SectionBlockInterface';
import { COLORS } from '~/types/AppData/Theme/ColorInterface';
import { getCSSColorBg, getCSSColorText } from '~/utilities/getCSS';
import AnalyticsContainer from '~/components/Shared/Analytics/AnalyticsContainer';
import { getOneIndexing } from '~/utilities/getOneIndexing';
import BackgroundImage from './BackgroundImage';
import { ConditionalWrapper } from '~/components/Shared/Utility/ConditionalWrapper/ConditionalWrapper';

const containerClasses = cva(
  'flex flex-col items-center relative overflow-hidden',
  {
    variants: {
      background: getCSSColorBg(),
      textColor: getCSSColorText(),
      lightText: {
        true: '[&_.card-footer]:text-lightGrey',
        false: '[&_.card-footer]:text-grey [&_.header-link]:text-blue',
      },
      isDarkGrey: {
        true: 'py-10 [&_hr]:border-grey',
        false: 'py-[30px]',
      },
      hasBackgroundImage: {
        true: '',
        false: '',
      },
      isBanner: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      {
        hasBackgroundImage: true,
        isBanner: false,
        class: 'min-h-[228px]',
      },
    ],
  }
);

const innerClasses = cva('w-full max-w-[1100px] px-5 flex flex-col', {
  variants: {
    isBanner: {
      true: '',
      false: 'gap-4',
    },
  },
});

const conditionalWrapperClasses = cva('relative', {
  variants: {
    isBanner: {
      true: '[&>:last-child]:mt-4 gap-1',
      false: '',
    },
  },
});

export default component$<
  SectionBlockInterface['props'] & { index: number; items?: any }
>(
  ({
    background,
    backgroundImage,
    textColor,
    index,
    items,
    alt,
    hasGradient = false,
    isBanner = false,
  }): JSX.Element => {
    const sectionHeader =
      items && items.length
        ? items.find((each: any) => each.type === 'SectionHeader')
        : null;
    const sectionName =
      sectionHeader && sectionHeader.props?.title
        ? sectionHeader.props.title
        : `Section-${getOneIndexing(index)}`;

    return (
      <div
        class={containerClasses({
          background,
          textColor,
          lightText: textColor === COLORS.WHITE,
          isDarkGrey: background === COLORS.DARK_GREY,
          hasBackgroundImage: !!backgroundImage,
          isBanner,
        })}
      >
        <AnalyticsContainer
          component_name={sectionName}
          component_type='Section'
          component_placement={getOneIndexing(index)}
          fireViewEvent={true}
        >
          <div class={innerClasses({ isBanner })}>
            {backgroundImage && (
              <BackgroundImage backgroundImage={backgroundImage} alt={alt} />
            )}
            {hasGradient && (
              <div class='absolute inset-0 bg-gradient-to-b from-[rgba(0,0,0,0.6)] to-[rgba(0,0,0,0.2)]' />
            )}
            {/* ConditionalWrapper added for the Banner component */}
            <ConditionalWrapper
              component='div'
              condition={!!(hasGradient || backgroundImage)}
              props={{ class: conditionalWrapperClasses({ isBanner }) }}
            >
              <Slot />
            </ConditionalWrapper>
          </div>
        </AnalyticsContainer>
      </div>
    );
  }
);
